import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Gift, Check, Lock, Users, Crown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useWalletSafe } from "@/hooks/useWalletSafe";

const MAX_CLAIMS = 10;

export function EarlyCreatorNFTClaim() {
  const { connected, publicKey } = useWalletSafe();
  const [claimCount, setClaimCount] = useState(0);
  const [hasClaimed, setHasClaimed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);

  useEffect(() => {
    const fetchClaimStatus = async () => {
      setCheckingStatus(true);
      try {
        // Get total claims
        const { count } = await supabase
          .from("early_creator_claims")
          .select("*", { count: "exact", head: true });
        
        setClaimCount(count || 0);

        // Check if current user has claimed
        if (connected && publicKey) {
          const walletAddress = publicKey.toBase58();
          const { data } = await supabase
            .from("early_creator_claims")
            .select("id")
            .eq("wallet_address", walletAddress)
            .single();
          
          setHasClaimed(!!data);
        }
      } catch (err) {
        console.error("Error fetching claim status:", err);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchClaimStatus();

    // Subscribe to realtime updates
    const channel = supabase
      .channel("early-creator-claims")
      .on("postgres_changes", { event: "*", schema: "public", table: "early_creator_claims" }, () => {
        fetchClaimStatus();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [connected, publicKey]);

  const handleClaim = async () => {
    if (!connected || !publicKey) {
      toast({
        title: "Connect Wallet",
        description: "Please connect your wallet to claim the NFT",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Session Expired",
          description: "Please reconnect your wallet",
          variant: "destructive",
        });
        return;
      }

      const response = await supabase.functions.invoke("claim-early-creator-nft", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to claim NFT");
      }

      const data = response.data;

      if (data.error) {
        toast({
          title: data.soldOut ? "Sold Out!" : data.alreadyClaimed ? "Already Claimed" : "Claim Failed",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "🎉 Early Creator Spot Claimed!",
        description: "Your Early Creator badge is now available. Claim it below!",
      });

      setHasClaimed(true);
      setClaimCount(prev => prev + 1);

    } catch (error: any) {
      console.error("Claim error:", error);
      toast({
        title: "Claim Failed",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isSoldOut = claimCount >= MAX_CLAIMS;
  const remainingSlots = MAX_CLAIMS - claimCount;

  if (isSoldOut && !hasClaimed) {
    return null; // Don't show if sold out and user hasn't claimed
  }

  return (
    <Card className="relative overflow-hidden border-primary/40 bg-gradient-to-br from-primary/10 via-accent/5 to-background">
      {/* Animated background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,hsl(var(--primary)/0.2),transparent_50%)] animate-pulse" />
      
      <CardContent className="p-4 md:p-6 relative">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          {/* NFT Image */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-xl blur-xl opacity-40 animate-pulse" />
            <img
              src="/early-creator-nft.jpeg"
              alt="Early Creator NFT"
              className="relative w-24 h-24 md:w-32 md:h-32 rounded-xl object-cover border-2 border-primary/50 shadow-lg"
            />
            {hasClaimed && (
              <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1.5">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}
          </div>

          {/* Content */}
            <div className="flex-1 text-center md:text-left space-y-2">
              <div className="flex items-center justify-center md:justify-start gap-2 flex-wrap">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg md:text-xl font-bold text-foreground">
                  Early Creator NFT
                </h3>
                <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30">
                  Exclusive
                </Badge>
                <Link to="/early-creators">
                  <Badge variant="outline" className="cursor-pointer hover:bg-amber-500/20 hover:border-amber-500/50 transition-colors">
                    <Crown className="w-3 h-3 mr-1" />
                    View All
                  </Badge>
                </Link>
              </div>

            <p className="text-sm text-muted-foreground">
              Be one of the first 10 creators on FLOW! This exclusive NFT is your badge of honor.
            </p>

            {/* Progress */}
            <div className="flex items-center justify-center md:justify-start gap-2 text-sm">
              <Users className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {claimCount}/{MAX_CLAIMS} claimed
              </span>
              {!isSoldOut && (
                <Badge variant="outline" className="text-xs border-accent/50 text-accent">
                  {remainingSlots} left
                </Badge>
              )}
              {isSoldOut && (
                <Badge variant="destructive" className="text-xs">
                  Sold Out
                </Badge>
              )}
            </div>
          </div>

          {/* Action */}
          <div className="flex-shrink-0">
            {checkingStatus ? (
              <Button disabled className="gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Loading...
              </Button>
            ) : hasClaimed ? (
              <Button disabled variant="secondary" className="gap-2 bg-green-500/20 text-green-400 border-green-500/30">
                <Check className="w-4 h-4" />
                Claimed
              </Button>
            ) : isSoldOut ? (
              <Button disabled variant="secondary" className="gap-2">
                <Lock className="w-4 h-4" />
                Sold Out
              </Button>
            ) : connected ? (
              <Button
                onClick={handleClaim}
                disabled={loading}
                className="gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Claiming...
                  </>
                ) : (
                  <>
                    <Gift className="w-4 h-4" />
                    Claim Free
                  </>
                )}
              </Button>
            ) : (
              <Button disabled variant="secondary" className="gap-2">
                Connect Wallet
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
